import { t } from '@lingui/core/macro';
import {
  BookingArrivalStatus,
  BookingCurrentEstimatedArrivalWindowRecord,
  BookingRecordDetailsCustomer
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { NoddiSkeleton } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';

export function BookingEstimatedArrivalInfo({
  booking,
  isActiveStep
}: {
  booking: BookingRecordDetailsCustomer;
  isActiveStep: boolean;
}) {
  const {
    data: currentEstimatedArrivalTime,
    isPending: isEstimatePending,
    error: currentEstimatedArrivalTimeError
  } = noddiAsync.useGet({
    type: URLKeys.getCurrentEstimatedArrivalTime,
    input: { slug: booking.slug }
  });

  if (isEstimatePending) {
    return <NoddiSkeleton className='h-8 w-full' />;
  }

  if (currentEstimatedArrivalTimeError) {
    return null;
  }

  return (
    <div className={cn(isActiveStep ? 'text-black' : 'text-muted-foreground', 'mb-2')}>
      <ArrivalTime arrival={currentEstimatedArrivalTime} />
    </div>
  );
}

const ArrivalTime = ({ arrival }: { arrival: BookingCurrentEstimatedArrivalWindowRecord }) => {
  const { start, end, status } = arrival;
  if (start && end && status) {
    const arrivalStart = noddiFormatDate(start, NoddiDateFormats.TIME);
    const arrivalEnd = noddiFormatDate(end, NoddiDateFormats.TIME);

    if (status === BookingArrivalStatus.BEHIND) {
      return (
        <p className='text-sm'>
          {t`We are running a bit behind schedule and might arrive a little later than expected. We expect to arrive between`}{' '}
          <strong>{arrivalStart}</strong> {t`and`} <strong>{arrivalEnd}</strong>.
        </p>
      );
    }
    if (status === BookingArrivalStatus.AHEAD) {
      return (
        <p className='text-sm'>
          {t`We are a bit ahead of schedule and might arrive a little earlier than expected. We expect to arrive between`}{' '}
          <strong>{arrivalStart}</strong> {t`and`} <strong>{arrivalEnd}</strong>.
        </p>
      );
    }
    return (
      <p className='text-sm'>
        {t`We will arrive between`} <strong>{arrivalStart}</strong> {t`and`} <strong>{arrivalEnd}</strong>.{' '}
        {t`You will be notified when we are on our way to you!`}
      </p>
    );
  }

  return (
    <p className='text-sm'>
      {t`We'll text you the day before with estimated arrival time. You will be notified when we are on our way to you!`}
    </p>
  );
};
